import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Proptypes from 'prop-types';
import FileDownload from 'js-file-download';
import { format } from 'date-fns';

import { ICCID_ERROR_LABELS } from '../../utils/iccid';

const OUTCOMES = {
  ASSIGNED: { label: 'Assigned', color: 'success' },
  UNASSIGNED: { label: 'Unassigned', color: 'success' },
  TRANSFERRED: { label: 'Transferred', color: 'success' },
  UNCHANGED: { label: 'Already done', color: 'default' },
  FAILED: { label: 'Failed', color: 'error' },
};

// A 200 does not mean everything moved — each ICCID is decided on its own, so
// the summary and per-item outcomes are what the operator has to see.
const IccidHolderChangeReport = ({ report, organizationNames, onDone }) => {
  const { summary, items } = report;
  const failures = items.filter(({ outcome }) => outcome === 'FAILED');

  const organizationLabel = (id) => (id == null ? '—' : organizationNames[id] || `#${id}`);

  // ICCIDs only, with the header the file reader accepts, so the operator can
  // fix the cause and re-upload exactly the ones that did not move.
  const downloadFailures = () => {
    const csv = ['iccid', ...failures.map(({ iccid }) => iccid)].join('\n');
    FileDownload(csv, `IccidFailed-${format(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.csv`);
  };

  return (
    <Box>
      {summary.failed === 0 ? (
        <Alert severity="success">
          {summary.changed} ICCIDs updated, {summary.unchanged} already in place.
        </Alert>
      ) : (
        <Alert severity="warning">
          {summary.changed} updated · {summary.unchanged} unchanged · {summary.failed} failed.
        </Alert>
      )}
      <Stack direction="row" spacing={1} sx={{ my: 2 }}>
        <Chip label={`Requested: ${summary.requested}`} />
        <Chip label={`Found in stock: ${summary.matched}`} />
      </Stack>

      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>ICCID</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Previous organization</TableCell>
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(({ iccid, outcome, organizationId, previousOrganizationId, errorCode }) => {
              const { label, color } = OUTCOMES[outcome] || { label: outcome, color: 'default' };
              return (
                <TableRow key={iccid}>
                  <TableCell>{iccid}</TableCell>
                  <TableCell>
                    <Chip size="small" label={label} color={color} variant="outlined" />
                  </TableCell>
                  <TableCell>{organizationLabel(organizationId)}</TableCell>
                  <TableCell>{organizationLabel(previousOrganizationId)}</TableCell>
                  <TableCell>{errorCode ? ICCID_ERROR_LABELS[errorCode] || errorCode : ''}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
        {failures.length > 0 && (
          <Button variant="outlined" onClick={downloadFailures}>
            Download failed ICCIDs
          </Button>
        )}
        <Button variant="contained" onClick={onDone}>
          Done
        </Button>
      </Stack>
    </Box>
  );
};

IccidHolderChangeReport.propTypes = {
  report: Proptypes.object,
  organizationNames: Proptypes.object,
  onDone: Proptypes.func,
};
export default IccidHolderChangeReport;
